import { c } from 'ttag';

import { BadgeCategory } from '@freelancelabs/components';

import { DashboardBlockedReason, MembershipDisplayStatus } from '../../graphql/__generated__/schema';

/**
 * Why a dashboard habilitation opens nothing today. A blocked line survives on purpose: it is
 * what answers « who had it » once the entry has left the catalogue, so every held entry is
 * rendered. Three reasons get three sentences because each calls for a different gesture.
 */
export interface BlockedNotice {
    /** The short qualifier, beside the entry. It names the state, never the gesture. */
    tag: string;
    /** What is true, then what to do about it. */
    sentence: string;
    /** `warning` throughout: a blocked habilitation is a configuration to finish, not an incident. */
    tone: BadgeCategory;
}

/** How a blocked habilitation reads, or null when it opens. */
export const blockedNotice = (reason: DashboardBlockedReason | null | undefined): BlockedNotice | null => {
    switch (reason) {
        case DashboardBlockedReason.Archived:
            return {
                tag: c('Dashboard habilitation').t`Entry archived`,
                sentence: c('Dashboard habilitation')
                    .t`The entry left this account's catalogue after it was granted. The habilitation survives on purpose — it is what answers « who had it » — and it opens nothing. Nothing un-archives an entry today: declare the same dashboard again on this account, grant the new entry, then withdraw this one.`,
                tone: 'warning',
            };
        case DashboardBlockedReason.ModuleNotEnabled:
            return {
                tag: c('Dashboard habilitation').t`Module closed`,
                sentence: c('Dashboard habilitation')
                    .t`The « Tableau de bord » module is not open on this account. Two distinct things: opening the module gives the account the section, granting a dashboard gives that section its content — so this is a configuration left half-done, not an anomaly. The module is opened from the users list filtered on this account.`,
                tone: 'warning',
            };
        case DashboardBlockedReason.PerimeterNotDeclared:
            return {
                tag: c('Dashboard habilitation').t`Perimeter not declared`,
                sentence: c('Dashboard habilitation')
                    .t`The catalogue entry declares no locked parameter carrying the account, so the token maker refuses to sign: signing without it would show the whole dashboard to whoever holds the link. It is the catalogue entry that has to be completed — today that means archiving it and declaring it again with its parameter.`,
                tone: 'warning',
            };
        default:
            return null;
    }
};

/**
 * The token maker also requires an active membership, a fourth condition the API deliberately
 * left out of `blockedReason`: it already sits on `PortalUserMembership.status`, so it is read
 * from the status the sheet shows and said once per account block. Null when the access is open.
 */
export const inactiveAccessSentence = (status: MembershipDisplayStatus | null | undefined): string | null => {
    switch (status) {
        // Both derive from membership.status = 'active'; never having signed in does not close anything.
        case MembershipDisplayStatus.Active:
        case MembershipDisplayStatus.ActiveNeverLoggedIn:
        case null:
        case undefined:
            return null;
        case MembershipDisplayStatus.Disabled:
            return c('Dashboard habilitation')
                .t`This person's account is disabled, so they get in nowhere and none of what they hold here opens. The habilitations stay granted and wait.`;
        default:
            return c('Dashboard habilitation')
                .t`This access is not active, and the token maker requires an active membership: nothing here opens until it is. The habilitations stay granted and wait.`;
    }
};
